import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import api from '../api/axios';
import { API_ENDPOINTS } from '../utils/constants';
import { useUser } from './UserContext';
import { useSocket } from './SocketContext';

const LiveClassContext = createContext();

export const useLiveClass = () => {
  const context = useContext(LiveClassContext);
  if (!context) {
    throw new Error('useLiveClass must be used within a LiveClassProvider');
  }
  return context;
};

const VALID_STATUSES = ['up', 'lv', 'scheduled', 'down', 'cp', 'ex'];
const HIDDEN_STATUSES = ['deleted', 'inactive', 'completed', 'expired', 'dl', 'cl'];

export const LiveClassProvider = ({ children }) => {
  const { isAuthenticated } = useUser();
  const socketCtx = useSocket();
  const notificationSocket = socketCtx?.notificationSocket ?? null;
  const [liveClasses, setLiveClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(0);

  const fetchingRef = useRef(false);
  const cacheTimeoutRef = useRef(null);
  const debounceRef = useRef(null);

  const fetchLiveClasses = useCallback(async (forceRefresh = false) => {
    if (!isAuthenticated) {
      setLiveClasses([]);
      setError(null);
      return [];
    }

    if (debounceRef.current && !forceRefresh) clearTimeout(debounceRef.current);
    if (fetchingRef.current && !forceRefresh) return liveClasses;

    const now = Date.now();
    if (!forceRefresh && (now - lastFetch) < 30000) return liveClasses;

    fetchingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const [legacyRes, moduleRes] = await Promise.allSettled([
        api.get(API_ENDPOINTS.LIVE_CLASSES),
        api.get(API_ENDPOINTS.MERITHUB_SESSIONS),
      ]);

      let allClasses = [];

      if (legacyRes.status === 'fulfilled') {
        const raw = legacyRes.value.data?.classes || legacyRes.value.data?.data || [];
        const legacy = (Array.isArray(raw) ? raw : []).map((c) => ({
          ...c,
          source: c.source || 'legacy',
        }));
        allClasses.push(...legacy);
      }

      if (moduleRes.status === 'fulfilled') {
        const raw = moduleRes.value.data?.data || [];
        const sessions = (Array.isArray(raw) ? raw : []).map((s) => ({
          ...s,
          source: 'merithub-module',
          platform: 'merithub',
        }));
        allClasses.push(...sessions);
        // MeritHub “webhook” is not called from the browser. MeritHub POSTs to your server;
        // this app only loads sessions via REST. Backend may sync status from MeritHub on that route.
        if (process.env.NODE_ENV === 'development') {
          const n = sessions.length;
          const live = sessions.filter((s) => s.meritHubBroadcastLive === true).length;
          const url = `${api.defaults?.baseURL || ''}${API_ENDPOINTS.MERITHUB_SESSIONS}`;
          console.info(
            `[LiveClassContext] GET ${url} → ${n} MeritHub session(s), ${live} with meritHubBroadcastLive (Join Live).`,
          );
        }
      }

      const validClasses = allClasses.filter((c) => {
        if (!c || !c._id) return false;
        const st = c.status?.toLowerCase();
        if (HIDDEN_STATUSES.includes(st)) return false;
        return VALID_STATUSES.includes(st) || !st;
      });

      setLiveClasses(validClasses);
      setLastFetch(now);
      setError(null);
      return validClasses;
    } catch (error) {
      let errorMessage = 'Failed to fetch live classes';
      if (error.response?.status === 401) errorMessage = 'Authentication required to view live classes';
      else if (error.response?.status === 403) errorMessage = 'Access denied to live classes';
      else if (error.response?.status >= 500) errorMessage = 'Server error. Please try again later';

      setError(errorMessage);
      if (error.response?.status === 401 || error.response?.status === 403) setLiveClasses([]);
      throw error;
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [lastFetch, isAuthenticated]);

  const getLiveClassesForCourse = useCallback((courseId) => {
    if (!courseId) return [];
    const normalized = courseId?.toString()?.trim();
    const hasMatchingId = (ids) => {
      if (!Array.isArray(ids) || !ids.length) return false;
      return ids.some((id) => {
        const idStr = typeof id === 'string' ? id.trim()
          : id && typeof id === 'object' ? (id._id || id.toString()).toString().trim()
          : id ? String(id).trim() : null;
        return idStr === normalized;
      });
    };

    const filtered = liveClasses.filter((lc) => {
      return hasMatchingId(lc.courseIds) || hasMatchingId(lc.batchIds);
    });

    return filtered.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
  }, [liveClasses]);

  const refreshLiveClasses = useCallback(() => fetchLiveClasses(true), [fetchLiveClasses]);

  const fetchLiveClassesRef = useRef(fetchLiveClasses);
  fetchLiveClassesRef.current = fetchLiveClasses;

  useEffect(() => {
    if (!notificationSocket || !isAuthenticated) return;
    const onMerithubLive = () => {
      fetchLiveClassesRef.current(true).catch(() => {});
    };
    notificationSocket.on('merithub-live:update', onMerithubLive);
    return () => notificationSocket.off('merithub-live:update', onMerithubLive);
  }, [notificationSocket, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const id = setInterval(() => {
      fetchLiveClassesRef.current(true).catch(() => {});
    }, 120000);
    return () => clearInterval(id);
  }, [isAuthenticated]);

  useEffect(() => {
    return () => {
      if (cacheTimeoutRef.current) clearTimeout(cacheTimeoutRef.current);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const contextValue = {
    liveClasses,
    loading,
    error,
    fetchLiveClasses,
    getLiveClassesForCourse,
    refreshLiveClasses,
    lastFetch,
  };

  return (
    <LiveClassContext.Provider value={contextValue}>
      {children}
    </LiveClassContext.Provider>
  );
};
